import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Loader2, Plane, ArrowUp, ArrowDown, X, Wand2, Trash2, Download, Play,
  FolderOpen, Check,
} from "lucide-react";

const ASPECTS = ["16:9", "9:16", "1:1"];
const RESOLUTIONS = ["480", "720", "1080"];
const MODELS = [
  { key: "mini", label: "Seedance Mini (fast)" },
  { key: "fast", label: "Seedance Fast" },
  { key: "pro", label: "Seedance Pro (best)" },
  { key: "veo", label: "Google Veo 3.1 (8s · best geometry)" },
];

export default function Flythrough() {
  const [moves, setMoves] = useState([]);
  const [title, setTitle] = useState("Untitled flythrough");
  const [aspect, setAspect] = useState("16:9");
  const [resolution, setResolution] = useState("720");
  const [model, setModel] = useState("mini");
  const [segDuration, setSegDuration] = useState(5);
  const [stops, setStops] = useState([]); // media_ids
  const [cameras, setCameras] = useState([]); // len = stops-1
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [list, setList] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  // import-from-project picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [activeSrc, setActiveSrc] = useState(null);
  const [sel, setSel] = useState(new Set());

  const loadList = async () => {
    try {
      const { data } = await api.get("/flythrough");
      setList(data.flythroughs || []);
    } catch (e) { /* no-op */ }
  };

  useEffect(() => {
    api.get("/flythrough/camera-moves").then((r) => setMoves(r.data.moves || [])).catch(() => {});
    loadList();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  // poll while any flythrough is generating
  useEffect(() => {
    const anyBusy = list.some((f) => f.status === "generating");
    if (anyBusy && !pollRef.current) {
      pollRef.current = setInterval(loadList, 6000);
    } else if (!anyBusy && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [list]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ids = data.uploaded || [];
      const nextStops = [...stops, ...ids];
      setStops(nextStops);
      setCameras(Array.from({ length: Math.max(0, nextStops.length - 1) }, (_, i) => cameras[i] || "push_in"));
      toast.success(`Added ${ids.length} photo${ids.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= stops.length) return;
    const ns = [...stops];
    [ns[i], ns[j]] = [ns[j], ns[i]];
    setStops(ns);
    setCameras(Array.from({ length: Math.max(0, ns.length - 1) }, (_, k) => cameras[k] || "push_in"));
  };
  const removeStop = (i) => {
    const ns = stops.filter((_, k) => k !== i);
    setStops(ns);
    setCameras(Array.from({ length: Math.max(0, ns.length - 1) }, (_, k) => cameras[k] || "push_in"));
  };
  const setCam = (i, val) => {
    const nc = [...cameras];
    nc[i] = val;
    setCameras(nc);
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setLoadingSources(true);
    try {
      const { data } = await api.get("/flythrough/photo-sources");
      const srcs = data.sources || [];
      setSources(srcs);
      setActiveSrc(srcs[0] || null);
    } catch (err) {
      toast.error("Couldn't load projects");
    } finally {
      setLoadingSources(false);
    }
  };

  const toggleSel = (id) => {
    const ns = new Set(sel);
    ns.has(id) ? ns.delete(id) : ns.add(id);
    setSel(ns);
  };

  const addSelected = () => {
    if (!activeSrc) return;
    const ordered = (activeSrc.media_ids || []).filter((id) => sel.has(id));
    if (!ordered.length) return;
    const nextStops = [...stops, ...ordered];
    setStops(nextStops);
    setCameras(Array.from({ length: Math.max(0, nextStops.length - 1) }, (_, k) => cameras[k] || "push_in"));
    toast.success(`Added ${ordered.length} photo${ordered.length === 1 ? "" : "s"} from project`);
    setPickerOpen(false);
    setSel(new Set());
  };

  const generate = async () => {
    if (stops.length < 2) return toast.error("Add at least 2 photos");
    setCreating(true);
    try {
      const { data } = await api.post("/flythrough", {
        title, aspect, resolution, model, stop_media_ids: stops, cameras, duration: segDuration,
      });
      await api.post(`/flythrough/${data.id}/generate`);
      toast.success("Generating your flythrough — this can take a few minutes per segment");
      setStops([]); setCameras([]); setTitle("Untitled flythrough");
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start");
    } finally {
      setCreating(false);
    }
  };

  const del = async (fid) => {
    if (!window.confirm("Delete this flythrough?")) return;
    await api.delete(`/flythrough/${fid}`);
    loadList();
  };

  // Robust download that works even when media is on a different origin than the app
  // (the plain <a download> attribute is ignored cross-origin, which looks like "nothing happens").
  const downloadMedia = async (mediaId, filename) => {
    try {
      const res = await fetch(mediaUrl(mediaId));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${mediaId}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // fallback: open in a new tab so the user can save manually
      window.open(mediaUrl(mediaId), "_blank");
    }
  };

  const segCount = Math.max(0, stops.length - 1);
  const estMin = segCount * (segDuration >= 10 ? 5 : 4);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="flythrough-page">
      <div className="max-w-6xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-8">
          <Link to="/app" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400 transition-colors" data-testid="back-to-studio">
            <ArrowLeft className="w-4 h-4" /> Back to Studio
          </Link>
          <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-500">Beta</span>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Plane className="w-6 h-6 text-amber-500" />
          <h1 className="font-display text-3xl md:text-4xl font-medium">Flythrough</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-8 max-w-2xl">
          Line up connected photos in order — patio → kitchen → living room — and we generate one
          continuous cinematic drone move that flies from each shot into the next. Pick a camera move
          for every hop. This is a separate tool; it doesn't touch your reel projects.
        </p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Builder */}
          <div className="border border-zinc-800 rounded-md p-5 bg-zinc-900/30" data-testid="flythrough-builder">
            <label className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Title</label>
            <Input data-testid="fly-title" value={title} onChange={(e) => setTitle(e.target.value)}
              className="bg-zinc-950 border-zinc-800 rounded-sm h-11 text-sm mt-1 mb-4" />

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Aspect</label>
                <select data-testid="fly-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-10 text-sm px-2 mt-1">
                  {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Quality</label>
                <select data-testid="fly-resolution" value={resolution} onChange={(e) => setResolution(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-10 text-sm px-2 mt-1">
                  {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}p</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Per hop</label>
                <select data-testid="fly-duration" value={segDuration} onChange={(e) => setSegDuration(Number(e.target.value))}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-10 text-sm px-2 mt-1">
                  <option value={5}>5s</option>
                  <option value={10}>10s</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Model</label>
              <select data-testid="fly-model" value={model} onChange={(e) => setModel(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm h-10 text-sm px-2 mt-1">
                {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>

            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="fly-file-input" />
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="fly-upload"
                className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-amber-500/60 rounded-sm py-3 text-sm text-zinc-300 transition-colors disabled:opacity-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Upload photos"}
              </button>
              <button onClick={openPicker} data-testid="fly-import"
                className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-amber-500/60 rounded-sm py-3 text-sm text-zinc-300 transition-colors">
                <FolderOpen className="w-4 h-4" /> From a project
              </button>
            </div>

            {/* Stops with camera move between them */}
            <div className="space-y-2 mb-5">
              {stops.map((id, i) => (
                <div key={`${id}-${i}`}>
                  <div className="flex items-center gap-3 bg-zinc-950/70 border border-zinc-800 rounded-sm p-2" data-testid={`fly-stop-${i}`}>
                    <span className="text-xs text-zinc-500 w-5 text-center">{i + 1}</span>
                    <img src={mediaUrl(id)} alt="" className="w-16 h-12 object-cover rounded-sm" />
                    <div className="flex-1" />
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-zinc-400 hover:text-amber-400 disabled:opacity-30" data-testid={`fly-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === stops.length - 1} className="p-1 text-zinc-400 hover:text-amber-400 disabled:opacity-30" data-testid={`fly-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => removeStop(i)} className="p-1 text-zinc-400 hover:text-red-400" data-testid={`fly-remove-${i}`}><X className="w-4 h-4" /></button>
                  </div>
                  {i < stops.length - 1 && (
                    <div className="flex items-center gap-2 pl-8 py-1.5">
                      <span className="text-amber-500/70 text-xs">↓ camera</span>
                      <select data-testid={`fly-camera-${i}`} value={cameras[i] || "push_in"} onChange={(e) => setCam(i, e.target.value)}
                        className="bg-zinc-950 border border-zinc-800 rounded-sm h-8 text-xs px-2 text-amber-300">
                        {moves.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {segCount > 0 && (
              <p className="text-xs text-zinc-500 mb-3">
                {segCount} segment{segCount === 1 ? "" : "s"} · ~{estMin} min to generate · uses crun credits
              </p>
            )}
            <Button data-testid="fly-generate" onClick={generate} disabled={creating || stops.length < 2}
              className="w-full h-12 rounded-full bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-2" /> Generate flythrough</>}
            </Button>
          </div>

          {/* Library */}
          <div data-testid="flythrough-library">
            <h2 className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-3">Your flythroughs</h2>
            {list.length === 0 && <p className="text-sm text-zinc-600">Nothing yet — build one on the left.</p>}
            <div className="space-y-4">
              {list.map((f) => (
                <div key={f.id} className="border border-zinc-800 rounded-md p-4 bg-zinc-900/30" data-testid={`fly-card-${f.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium">{f.title}</p>
                      <p className="text-[11px] text-zinc-500">{f.aspect} · {f.resolution}p · {(f.segments || []).length} segments</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill status={f.status} />
                      <button onClick={() => del(f.id)} className="p-1 text-zinc-500 hover:text-red-400" data-testid={`fly-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {f.status === "generating" && (
                    <div className="text-xs text-zinc-400 space-y-1 mt-2">
                      {(f.segments || []).map((s) => (
                        <div key={s.index} className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${s.status === "done" ? "bg-emerald-500" : s.status === "failed" ? "bg-red-500" : "bg-amber-500 animate-pulse"}`} />
                          Segment {s.index + 1}: {s.status} ({s.camera.replace("_", " ")})
                        </div>
                      ))}
                    </div>
                  )}

                  {f.status === "done" && f.final_media_id && (
                    <div className="mt-2">
                      <video src={mediaUrl(f.final_media_id)} controls playsInline preload="metadata"
                        className="w-full rounded-sm bg-black" data-testid={`fly-video-${f.id}`} />
                      <button onClick={() => downloadMedia(f.final_media_id, `${(f.title || "flythrough").replace(/\s+/g, "-")}.mp4`)}
                        className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 mt-2" data-testid={`fly-download-${f.id}`}>
                        <Download className="w-3.5 h-3.5" /> Download final ({f.duration ? `${f.duration.toFixed(1)}s` : "mp4"})
                      </button>

                      {/* individual segment clips */}
                      {(f.segments || []).some((s) => s.clip_media_id) && (
                        <div className="mt-3 border-t border-zinc-800 pt-3">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Individual clips</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {(f.segments || []).filter((s) => s.clip_media_id).map((s) => (
                              <div key={s.index} data-testid={`fly-seg-${f.id}-${s.index}`} className="bg-zinc-950/60 rounded-sm p-1.5">
                                <video src={mediaUrl(s.clip_media_id)} controls playsInline preload="none"
                                  className="w-full rounded-sm bg-black" />
                                <button onClick={() => downloadMedia(s.clip_media_id, `${(f.title || "clip").replace(/\s+/g, "-")}-hop${s.index + 1}.mp4`)}
                                  className="inline-flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300 mt-1"
                                  data-testid={`fly-seg-download-${f.id}-${s.index}`}>
                                  <Download className="w-3 h-3" /> Hop {s.index + 1} · {s.camera.replace("_", " ")}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {f.status === "error" && (
                    <p className="text-xs text-red-400 mt-1">{f.error || "Something went wrong"}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fly-picker">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <h3 className="text-sm font-medium">Import photos from a project</h3>
              <button onClick={() => { setPickerOpen(false); setSel(new Set()); }} className="text-zinc-400 hover:text-white" data-testid="fly-picker-close"><X className="w-5 h-5" /></button>
            </div>
            {loadingSources ? (
              <div className="flex-1 flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
            ) : sources.length === 0 ? (
              <div className="flex-1 flex items-center justify-center py-16 text-sm text-zinc-500">No projects with photos yet.</div>
            ) : (
              <div className="flex-1 flex min-h-0">
                <div className="w-56 border-r border-zinc-800 overflow-y-auto p-2 shrink-0">
                  {sources.map((s) => (
                    <button key={s.project_id} onClick={() => { setActiveSrc(s); setSel(new Set()); }}
                      data-testid={`fly-src-${s.project_id}`}
                      className={`w-full text-left px-2 py-2 rounded-sm mb-1 flex items-center gap-2 ${activeSrc?.project_id === s.project_id ? "bg-amber-500/15 border border-amber-500/40" : "hover:bg-zinc-800/60 border border-transparent"}`}>
                      {s.cover && <img src={mediaUrl(s.cover)} alt="" className="w-9 h-9 object-cover rounded-sm shrink-0" />}
                      <span className="min-w-0">
                        <span className="block text-xs text-zinc-200 truncate">{s.title}</span>
                        <span className="block text-[10px] text-zinc-500">{s.count} photos</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                    {(activeSrc?.media_ids || []).map((id) => (
                      <button key={id} onClick={() => toggleSel(id)} data-testid={`fly-pick-${id}`}
                        className={`relative aspect-square rounded-sm overflow-hidden border-2 ${sel.has(id) ? "border-amber-500" : "border-transparent hover:border-zinc-600"}`}>
                        <img src={mediaUrl(id)} alt="" className="w-full h-full object-cover" />
                        {sel.has(id) && <span className="absolute top-1 right-1 bg-amber-500 text-zinc-950 rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-500">{sel.size} selected · added in the order they appear</span>
              <Button data-testid="fly-picker-add" onClick={addSelected} disabled={sel.size === 0}
                className="h-9 rounded-full bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-40">
                Add {sel.size > 0 ? sel.size : ""} to flythrough
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    created: ["Draft", "text-zinc-400 border-zinc-700"],
    generating: ["Generating", "text-amber-400 border-amber-500/50"],
    done: ["Ready", "text-emerald-400 border-emerald-500/50"],
    error: ["Error", "text-red-400 border-red-500/50"],
  };
  const [label, cls] = map[status] || map.created;
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>;
}
